import { useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { CalendarDays, Phone, ShieldCheck } from "lucide-react";

import { ScheduleProvider } from "@/components/ScheduleModal";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Seo } from "@/components/Seo";

const BOOKING_URL = "https://api.leadconnectorhq.com/widget/booking/qradOZhUywW2EZ7FBIbw";
const PHONE_HREF = "tel:+16234141766";
const PATH = "/schedule";

function ScheduleContent() {
  // Forward any incoming UTM / query parameters straight into the GHL booking
  // embed so attribution (e.g. gbp_chandler) carries through to the lead.
  const bookingSrc = useMemo(() => {
    const search = typeof window !== "undefined" ? window.location.search : "";
    return search ? `${BOOKING_URL}${search}` : BOOKING_URL;
  }, []);

  // Load the GoHighLevel embed helper once (same script the modal uses) so the
  // booking widget resizes/behaves identically to the existing experience.
  useEffect(() => {
    if (!document.getElementById("ghl-booking-embed")) {
      const s = document.createElement("script");
      s.id = "ghl-booking-embed";
      s.src = "https://link.msgsndr.com/js/embed.js";
      s.async = true;
      document.body.appendChild(s);
    }
    if (!document.getElementById("ghl-form-embed")) {
      const s2 = document.createElement("script");
      s2.id = "ghl-form-embed";
      s2.src = "https://link.msgsndr.com/js/form_embed.js";
      s2.async = true;
      document.body.appendChild(s2);
    }
  }, []);

  return (
    <main data-testid="schedule-page" className="relative pt-28 sm:pt-32 pb-20 px-5 sm:px-8">
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="text-center"
        >
          <span className="mono text-[11px] uppercase tracking-[0.35em] text-[#d4af37]">
            John Campbell Insurance
          </span>
          <h1
            data-testid="schedule-heading"
            className="serif text-4xl sm:text-5xl text-[#f4f0ea] mt-4 leading-tight"
          >
            Schedule a <span className="text-[#d4af37] italic">Consultation</span>
          </h1>
          <p className="text-[#a3a3a3] text-base sm:text-lg mt-5 max-w-2xl mx-auto">
            Choose a time that works for you. A relaxed, no-pressure 15-minute
            conversation to understand your options — at no cost and no obligation.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-6 text-[#737373] mono text-[10px] uppercase tracking-[0.25em]">
            <span className="flex items-center gap-2"><CalendarDays size={13} className="text-[#d4af37]" /> 15-Minute Consultation</span>
            <span className="flex items-center gap-2"><ShieldCheck size={13} className="text-[#d4af37]" /> No Cost · No Obligation</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 xl:-mx-[136px] bg-[#0a0a0a] border border-[#d4af37]/30 rounded-sm overflow-hidden shadow-[0_30px_80px_rgba(0,0,0,0.6)]"
        >
          <div className="flex items-center gap-3 px-6 sm:px-8 py-5 border-b border-white/10">
            <CalendarDays size={18} className="text-[#d4af37]" />
            <div className="flex flex-col leading-none">
              <span className="serif text-lg text-[#f4f0ea]">15-Minute Life Insurance Consultation</span>
              <span className="mono text-[9px] uppercase tracking-[0.3em] text-[#737373] mt-1">John Campbell Insurance</span>
            </div>
          </div>
          <div className="bg-[#0a0a0a]">
            {/* Height is set by GHL's form_embed.js resizer; wrapper grows with it. */}
            <iframe
              src={bookingSrc}
              title="15-Minute Life Insurance Consultation"
              data-testid="schedule-iframe"
              className="block w-full border-0"
              style={{ minHeight: 820 }}
              scrolling="yes"
            />
          </div>
        </motion.div>

        <div className="text-center mt-8">
          <a
            href={PHONE_HREF}
            data-testid="schedule-call-link"
            className="inline-flex items-center gap-2 text-[#d4af37] hover:text-[#f4f0ea] transition-colors mono text-xs uppercase tracking-[0.25em]"
          >
            <Phone size={14} /> Prefer to talk? Call John at (623) 414-1766
          </a>
        </div>
      </div>
    </main>
  );
}

export default function Schedule() {
  return (
    <div className="App grain bg-[#030303] min-h-screen">
      <Seo
        title="Schedule a Consultation | John Campbell Insurance"
        description="Book a no-cost, no-obligation 15-minute life insurance consultation with John Campbell. Choose a time that works for you."
        path={PATH}
        type="website"
      />
      <ScheduleProvider>
        <Navbar />
        <ScheduleContent />
        <Footer />
      </ScheduleProvider>
    </div>
  );
}
